import { Pipe, PipeTransform } from '@angular/core';
import { getEnrollmentMessage, isValidSubscription } from '@app/utilities/_index';

import { CardTypeCallToActions } from '@app/enums/_index';
import { Learning_Type } from '@app/enums/learning-type.enum';
import { User } from '@app/models/user.model';

@Pipe({
  name: 'sessionEnrollmentCallToActions',
  pure: false,
  standalone: false,
})
export class SessionEnrollmentCallToActionsPipe implements PipeTransform {
  /**
   * Session Enrollment Status Pipe.
   * NOTE: Pipe must be impure due to the aynchronous nature of the user service which contains the user subscription and enrollments.
   * @param learningType - learning type to transform to new display type
   * @param user - If user is signed in, then the transformed type will return a different display type.
   * @param subscriptionId - Subscription id used to check if the user has this subscription.
   * @param relatedSubscriptionIds - Related subscriptions used to check if the course can appear in 1 to N subscriptions
   * @param courseId - Course id used to check if the use has this enrollment already to determine enrollment status
   * @param sessionId - Session id used to chck if the user has the enrolled session in enrollments.
   * @param countEnroll - Current number of users enrolled in a session.
   * @param maxEnroll - Maximum number of users that can be enrolled in a session.
   * @returns - transformed call to action string
   */
  transform(
    learningType: string,
    user: User,
    subscriptionId: number,
    relatedSubscriptionIds: number[],
    courseId: number,
    sessionId: number,
    countEnroll: number,
    maxEnroll: number
  ): string {
    switch (learningType) {
      case Learning_Type.CERTIFICATION_EXAM:
        if (user) {
          // NOTE: Certification exams do not use session or seat logic
          const enrollment =
            user.enrollments &&
            user.enrollments.length > 0 &&
            user.enrollments.find((courseEnrollment) => courseEnrollment.id === courseId);

          if (enrollment) {
            // Return enrollment message for certification exam
            return getEnrollmentMessage(enrollment.learningType, enrollment.status);
          }

          // Logged in but not enrolled in the certification exam
          return CardTypeCallToActions.ENROLL;
        } else {
          return CardTypeCallToActions.SIGN_IN;
        }

      case Learning_Type.INSTRUCTOR_LED:
      case Learning_Type.INSTRUCTOR_LED_IN_PERSON:
      case Learning_Type.INSTRUCTOR_LED_VIRTUAL:
      case Learning_Type.IN_PERSON:
      case Learning_Type.VIRTUAL:
      case Learning_Type.WORKSHOP:
        if (user) {
          if (user && user.activeSubscriptions && user.activeSubscriptions.length > 0) {
            // Check if user has active subscription id that matches incoming subscription id
            const isSubscribed = isValidSubscription(relatedSubscriptionIds, subscriptionId, user);
            if (isSubscribed) {
              // User is subscribed to this subscription id
              // NOTE: Check if the user enrollment message to update this if they are already in enrolled.
              const enrollment =
                user.enrollments &&
                user.enrollments.length > 0 &&
                user.enrollments.find((courseEnrollment) => courseEnrollment.id === courseId);
              if (enrollment) {
                // Check enrollment sessions and check if the incoming session id
                const enrolledSession =
                  enrollment.enrolledSessions && enrollment.enrolledSessions.length > 0;
                if (enrolledSession) {
                  if (enrollment.enrolledSessions.find((session) => session.id === sessionId)) {
                    return getEnrollmentMessage(enrollment.learningType, enrollment.status);
                  } else {
                    if (maxEnroll > countEnroll) {
                      return CardTypeCallToActions.ENROLL; // Seats still left for the session to enroll
                    } else {
                      return CardTypeCallToActions.SOLD_OUT; // Seats not left and they are sold out
                    }
                  }
                } else {
                  // Not enrolled in session.
                  // Check if the Seats are left for the session to Enroll.
                  if (maxEnroll > countEnroll) {
                    return CardTypeCallToActions.ENROLL; // Seats still left for the session to enroll
                  } else {
                    return CardTypeCallToActions.SOLD_OUT; // Seats not left and they are sold out
                  }
                }
              } else {
                // Check if the Seats are left for the session to Enroll.
                if (maxEnroll > countEnroll) {
                  return CardTypeCallToActions.ENROLL; // Seats still left for the session to enroll
                } else {
                  return CardTypeCallToActions.SOLD_OUT; // Seats not left and they are sold out
                }
              }
            } else {
              // User is not subscribed to this subscription id
              return CardTypeCallToActions.SUBSCRIPTION_REQUIRED;
            }
          } else {
            // User is not subscribed to this subscription id
            return CardTypeCallToActions.SUBSCRIPTION_REQUIRED;
          }
        } else {
          return CardTypeCallToActions.SIGN_IN;
        }
    }
  }
}
